// disable strict mode
"use strict";
import {
  communityFeedEl,
  communityNavEl,
  communityProfileFeedEl,
  deleteLevelDialogCancelBtnEl,
  deleteLevelDialogDeleteBtnEl,
  deleteLevelDialogEl,
  gameProfileUnameEl,
  gameProfileImgEl,
  gameProfileEl,
} from "../elements";
import { withSpinner } from "../spinner";
import { stack } from "../nav";
import store from "../store";
import { addTouchEvent } from "../events";
import { play } from "../map";
import { catchErrorWithStack, throwErrorWithStack } from "../crashlytics";
import { formatNumber, prettyDateFormat } from "../utils";
import { initRipple } from "../ripple";
import { addDropDown, updateDropDown } from "../dropdown";
import { refreshProfile, showProfile } from "../profile";
import { addClickToBtns } from "../sound";
import cachedFirestore from "../cachedFirestore";
import { info } from "../levels";
import { POSITIONS } from "../info";
import { showInfoSnackbar } from "../snackbar";
import { GAME_MODE, gameState, levelDef } from "../game/gameState";
import { increment } from "firebase/firestore";
import { requireAuth } from "../auth";
import { updateStyles } from "../nav/styles";
import { yieldWithPriority } from "../promise";
import { removeFavorite, storeFavorite } from ".";
import { IS_WEB } from "../config";
import { autoTranslate, resetLangAttributeOfLeafs } from "../translate";
import { getLogger } from "../logging";

const log = getLogger("community/feed");

const NUM_COLUMNS_INIT = 10;
const NUM_ROWS = 1;
const NUM_COLUMNS_SCROLL = 10;
let numColsRenderedGlob = 0;
let lastVisibleGlob = null;
let levelToDeleteGlob = null;

let i_ = 0;
const FEED = Object.freeze({
  USER: i_++,
  NEW: i_++,
  TRENDY: i_++,
  TOP: i_++,
  //   FOLLOWING: i_++,
  FAVORITES: i_++,
});
const FEED_MAP = Object.freeze({
  [FEED.USER]: "communityLevelsUser",
  [FEED.NEW]: "communityLevelsNew",
  [FEED.TRENDY]: "communityLevelsTrendy",
  [FEED.TOP]: "communityLevelsTop",
  //   [FEED.FOLLOWING]: "communityLevelsFollowing",
  [FEED.FAVORITES]: "communityLevelsFavorites",
});

function getCurrentFeed() {
  const feedKey = FEED_MAP[store.communityNavIdx];
  const feed = store[feedKey];
  if (!feed) return [];
  return feed;
}

let timeoutGlob = null;
let lastScrollPositionGlob = 0;
let handlingScrollGlob = false;
const scrollCallbackFuncs = {};

export function updateScrollPosition() {
  const startPosition =
    -communityFeedEl.scrollLeft +
    -communityNavEl.clientWidth +
    0.3 * store.innerHeight;

  if (startPosition < 0)
    communityNavEl.style.transform = "translateX(" + startPosition + "px)";
  else communityNavEl.style.transform = null;
}

async function _scrollCallback(feedEl, currentScroll, totalWidth) {
  if (currentScroll >= totalWidth - 2 && !handlingScrollGlob) {
    handlingScrollGlob = true;
    lastScrollPositionGlob = currentScroll;
    const start = numColsRenderedGlob * NUM_ROWS;
    const end = (numColsRenderedGlob + NUM_COLUMNS_SCROLL) * NUM_ROWS;
    let feed = getCurrentFeed().slice(start, end);
    if (!feed.length) await fetchFeeds(true);
    feed = getCurrentFeed().slice(start, end);
    const numNewCols = Math.min(
      Math.ceil(feed.length / NUM_ROWS),
      NUM_COLUMNS_SCROLL
    );
    renderPlaceholderFeeds(feedEl, numNewCols, numColsRenderedGlob);
    drawLevels({ feedEl, feed, offSet: numColsRenderedGlob });
    numColsRenderedGlob += numNewCols;
    handlingScrollGlob = false;
    await yieldWithPriority("user-visible");
    autoTranslate();
  }
}

function getScrollCallback(feedEl = communityFeedEl) {
  if (scrollCallbackFuncs[feedEl]) return scrollCallbackFuncs[feedEl];
  const func = function scrollCallback() {
    if (store.screenName !== "community_feed") return;

    const totalWidth = feedEl.scrollWidth;
    const currentScroll = feedEl.scrollLeft + feedEl.clientWidth;

    if (currentScroll !== lastScrollPositionGlob) {
      if (timeoutGlob !== null) clearTimeout(timeoutGlob);
      timeoutGlob = setTimeout(
        _scrollCallback,
        100,
        feedEl,
        currentScroll,
        totalWidth
      ); // Adjust the timeout as needed
    }

    updateScrollPosition();
  };
  scrollCallbackFuncs[feedEl] = func;
  return func;
}

export async function showCommunityFeed() {
  stack.push("community_feed");
  updateStyles();
  await refreshCommunityFeed();
}

export async function refreshCommunityFeed() {
  try {
    numColsRenderedGlob = 0;
    lastVisibleGlob = null;
    communityFeedEl.innerHTML = "";

    communityProfileFeedEl.src =
      store.profile.thumbnailUrl ||
      store.user.photoUrl ||
      "img/placeholder.jpg";

    const scrollCallback = getScrollCallback(communityFeedEl);
    communityFeedEl.removeEventListener("scroll", scrollCallback);
    await withSpinner(fetchFeeds());
    const feed = getCurrentFeed().slice(0, NUM_COLUMNS_INIT * NUM_ROWS);
    renderPlaceholderFeeds(
      communityFeedEl,
      Math.min(feed.length, NUM_COLUMNS_INIT)
    );
    drawLevels({ feedEl: communityFeedEl, feed });
    numColsRenderedGlob += NUM_COLUMNS_INIT;

    timeoutGlob = null;
    lastScrollPositionGlob = 0;

    communityFeedEl.addEventListener("scroll", scrollCallback);
    await yieldWithPriority("user-visible");
    updateScrollPosition();
  } catch (error) {
    throwErrorWithStack(error);
  } finally {
    await yieldWithPriority("user-visible");
    autoTranslate();
  }
}

async function fetchFeeds(append = false) {
  try {
    let queryConstraints = [
      {
        type: "limit",
        limit:
          (lastVisibleGlob ? NUM_COLUMNS_SCROLL : NUM_COLUMNS_INIT) * NUM_ROWS,
      },
      {
        type: "orderBy",
        fieldPath: "createdAt",
        directionStr: "desc",
      },
    ];
    let compositeFilter = __ADMIN__
      ? null
      : {
          type: "and",
          queryConstraints: [
            {
              type: "where",
              fieldPath: "isPublic",
              opStr: "==",
              value: true,
            },
          ],
        };
    switch (store.communityNavIdx) {
      case FEED.USER:
        compositeFilter = {
          type: "and",
          queryConstraints: [
            {
              type: "where",
              fieldPath: "creatorId",
              opStr: "==",
              value: store.user.uid,
            },
          ],
        };
        break;
      case FEED.FOLLOWING:
        {
          const dataList = await cachedFirestore.getCollection({
            reference: `profile/${store.user.uid}/following`,
            queryConstraints: [
              {
                type: "limit",
                limit: 29,
              },
            ],
          });
          const followingIds = dataList.map(({ id }) => id);
          compositeFilter = {
            type: "and",
            queryConstraints: [
              {
                type: "where",
                fieldPath: "creatorId",
                opStr: "in",
                value: [store.user.uid, ...followingIds],
              },
            ],
          };
          if (IS_WEB && !__ADMIN__) {
            // TODO: add this to Android when it supports multiple composite filters
            compositeFilter.queryConstraints.push({
              type: "where",
              fieldPath: "isPublic",
              opStr: "==",
              value: true,
            });
          }
        }
        break;
      case FEED.FAVORITES:
        if (!store.levelsFavorited.size) {
          const feedKey = FEED_MAP[store.communityNavIdx];
          store.set(feedKey, []);
          return;
        }
        compositeFilter = {
          type: "and",
          queryConstraints: [
            {
              type: "where",
              fieldPath: "levelId",
              opStr: "in",
              value: [...store.levelsFavorited],
            },
          ],
        };
        if (IS_WEB && !__ADMIN__) {
          // TODO: add this to Android when it supports multiple composite filters
          compositeFilter.queryConstraints.push({
            type: "where",
            fieldPath: "isPublic",
            opStr: "==",
            value: true,
          });
        }
        break;
      case FEED.TRENDY:
        queryConstraints = [
          {
            type: "limit",
            limit:
              (lastVisibleGlob ? NUM_COLUMNS_SCROLL : NUM_COLUMNS_INIT) *
              NUM_ROWS,
          },
          {
            type: "orderBy",
            fieldPath: "trendynessScore",
            directionStr: "desc",
          },
        ];
        break;
      case FEED.TOP:
        queryConstraints = [
          {
            type: "limit",
            limit:
              (lastVisibleGlob ? NUM_COLUMNS_SCROLL : NUM_COLUMNS_INIT) *
              NUM_ROWS,
          },
          {
            type: "orderBy",
            fieldPath: "score",
            directionStr: "desc",
          },
        ];
        break;
      default:
        break;
    }

    if (lastVisibleGlob)
      queryConstraints.push({
        type: "startAfter",
        reference: lastVisibleGlob.path,
      });
    let snapshots = await cachedFirestore.getCollection({
      reference: "communityLevels",
      queryConstraints,
      compositeFilter,
    });
    if (!IS_WEB && !__ADMIN__) {
      // TODO: remove this when Android supports multiple composite filters
      snapshots = snapshots.filter(({ data }) => data.isPublic);
    }
    if (!snapshots.length) return;
    lastVisibleGlob = snapshots[snapshots.length - 1];
    let levels = levelSnapshotsToLevels(snapshots);
    if (append) levels = [...getCurrentFeed(), ...levels];
    const feedKey = FEED_MAP[store.communityNavIdx];
    store.set(feedKey, levels);
  } catch (error) {
    catchErrorWithStack(error);
  }
}

export function levelSnapshotsToLevels(snapshots) {
  const levels = snapshots.map((snapshot) => snapshot.data);
  return levels.map(
    ({
      isPublic,
      description,
      creatorName,
      creatorUname,
      creatorId,
      createdAt,
      wonCount,
      lostCount,
      uniqueRatingCount,
      avgRating,
      winToLossRatio,
      levelDef: levelDefStr,
      levelId,
      thumbnailUrl,
      creatorThumbnailUrl,
    }) => {
      const parsedLevelDef = JSON.parse(levelDefStr);
      const halfFieldSizeFloor = Math.floor(parsedLevelDef.fieldSize / 2);
      return {
        isPublic,
        description,
        creatorName,
        creatorUname,
        creatorId,
        createdAt,
        wonCount,
        lostCount,
        uniqueRatingCount,
        avgRating,
        winToLossRatio,
        thumbnailUrl,
        creatorThumbnailUrl,
        levelDef: {
          ...parsedLevelDef,
          id: levelId,
          info:
            description &&
            info(
              [halfFieldSizeFloor, halfFieldSizeFloor],
              description,
              0,
              POSITIONS.CENTER
            ),
        },
      };
    }
  );
}

export function renderPlaceholderFeeds(
  feedEl = communityFeedEl,
  numColumns = NUM_COLUMNS_INIT,
  offSet = 0,
  idPreffix = "community-feed"
) {
  const fragment = document.createDocumentFragment();
  Array.from({ length: numColumns }, (_, i) => i + offSet).forEach((col) => {
    const divColumn = document.createElement("div");
    divColumn.className = "community-feed-column";
    divColumn.id = `${idPreffix}-column-${col}`;
    Array.from({ length: NUM_ROWS }, (_, i) => i).forEach((row) => {
      const divItem = document.createElement("div");
      const divItemThumbnail = document.createElement("div");
      divItem.classList.add("community-feed-item");
      divItem.id = `${idPreffix}-item-${col}-${row}`;
      divItem.classList.add("button");
      divItemThumbnail.classList.add("community-feed-item-thumbnail", "ripple");
      divItem.appendChild(divItemThumbnail);
      divColumn.appendChild(divItem);
    });
    fragment.appendChild(divColumn);
    fragment.appendChild(divColumn);
    fragment.appendChild(divColumn);
  });
  feedEl.appendChild(fragment);
}

export async function drawLevels({
  feedEl = communityFeedEl,
  feed,
  offSet = 0,
  showFeed = showCommunityFeed,
  idPreffix = "community-feed",
}) {
  const feedCol = feed.reduce((result, value, index, array) => {
    if (index % NUM_ROWS === 0)
      result.push(array.slice(index, index + NUM_ROWS));
    return result;
  }, []);
  feedCol.forEach((colDef, col) => {
    const divColumn = document.getElementById(
      `${idPreffix}-column-${col + offSet}`
    );
    divColumn.className = "community-feed-column";
    colDef.forEach(
      (
        {
          isPublic,
          description,
          creatorName,
          creatorUname,
          creatorId,
          wonCount,
          lostCount,
          uniqueRatingCount,
          avgRating,
          winToLossRatio,
          createdAt,
          levelDef: levelDef_,
          thumbnailUrl,
          creatorThumbnailUrl,
        },
        row
      ) => {
        const fragment = document.createDocumentFragment();
        const divItem = feedEl.querySelector(
          `#${idPreffix}-item-${col + offSet}-${row}`
        );
        if (divItem.getAttribute("populated")) {
          return;
        }
        divItem.setAttribute("populated", "true");
        const divItemThumbnail = divItem.querySelector(
          ".community-feed-item-thumbnail"
        );
        const metadata = {
          isPublic,
          description,
          creatorName,
          creatorUname,
          creatorId,
          wonCount,
          lostCount,
          uniqueRatingCount,
          avgRating,
          winToLossRatio,
          createdAt,
          thumbnailUrl,
          creatorThumbnailUrl,
        };
        addTouchEvent(
          divItemThumbnail,
          requireAuth(async () => {
            gameState.mode = GAME_MODE.COMMUNITY;
            levelDef_.metadata = metadata;
            gameProfileImgEl.src = creatorThumbnailUrl || "img/placeholder.jpg";
            gameProfileUnameEl.textContent = creatorUname;
            await play({ level: levelDef_, delay: 100 });
            updateStyles();
          })
        );
        const hasPlayed = store.levelsPlayed.has(levelDef_.id);
        const hasWon = store.levelsWon.has(levelDef_.id);
        const divItemStatusContainer = document.createElement("div");
        divItemStatusContainer.classList.add(
          "community-feed-item-status-container"
        );
        if (!isPublic) {
          divItemStatusContainer.classList.add("private");
          divItemStatusContainer.classList.add("ripple");
          const spanItemWon = document.createElement("span");
          spanItemWon.classList.add(
            "material-symbols-outlined",
            "outlined",
            "community-feed-item-status",
            "notranslate"
          );
          spanItemWon.innerHTML = "lock";
          divItemStatusContainer.appendChild(spanItemWon);
          divItemThumbnail.appendChild(divItemStatusContainer);
        }
        if (hasWon) {
          divItemStatusContainer.classList.add("won");
          divItemStatusContainer.classList.add("ripple");
          const spanItemWon = document.createElement("span");
          spanItemWon.classList.add(
            "material-symbols-outlined",
            "outlined",
            "community-feed-item-status",
            "notranslate"
          );
          spanItemWon.innerHTML = "trophy";
          divItemStatusContainer.appendChild(spanItemWon);
          divItemThumbnail.appendChild(divItemStatusContainer);
        } else if (hasPlayed) {
          divItemStatusContainer.classList.add("ripple");
          const spanItemWon = document.createElement("span");
          spanItemWon.classList.add(
            "material-symbols-outlined",
            "community-feed-item-status",
            "notranslate"
          );
          //   spanItemWon.classList.add("text-on-white");
          spanItemWon.innerHTML = "barefoot";
          divItemStatusContainer.appendChild(spanItemWon);
          divItemThumbnail.appendChild(divItemStatusContainer);
        }
        const divItemCaptionTop = document.createElement("div");
        divItemCaptionTop.classList.add("community-feed-item-caption-top");

        const divItemMoreContainer = document.createElement("div");

        const createFavorite = () => {
          const divItemFavContainer = document.createElement("div");
          divItemFavContainer.id = `${idPreffix}-item-fav-${
            col + offSet
          }-${row}`;
          const spanItemFav = document.createElement("span");
          spanItemFav.classList.add("text-outline-semisoft");
          divItemFavContainer.classList.add(
            "community-feed-item-fav-container"
          );
          spanItemFav.classList.add(
            "material-symbols-outlined",
            "community-feed-item-fav",
            "notranslate"
          );
          spanItemFav.innerHTML = "favorite";
          divItemFavContainer.appendChild(spanItemFav);
          divItemThumbnail.appendChild(divItemFavContainer);
        };
        const deleteFavorite = async () => {
          const divItemFavContainer = document.getElementById(
            `${idPreffix}-item-fav-${col + offSet}-${row}`
          );
          if (divItemFavContainer) divItemFavContainer.remove();
          else log.warn("Failed to delete favorite element");
          removeFavorite(levelDef_.id);
          cachedFirestore.clearCollectionCache("communityLevels");
          if (store.communityNavIdx === FEED.FAVORITES) refreshCommunityFeed();
        };

        const isFavorite = () => store.levelsFavorited.has(levelDef_.id);

        const favoriteCallback = () => {
          if (isFavorite()) deleteFavorite();
          else if (storeFavorite(levelDef_.id)) {
            createFavorite();
            cachedFirestore.clearCollectionCache("communityLevels");
            if (store.communityNavIdx === FEED.FAVORITES)
              refreshCommunityFeed();
          }
        };

        const getContentList = () => {
          const contentList = [];
          if (creatorId === store.user.uid) {
            if (!isPublic)
              contentList.push({
                innerHTML: `<span class="material-symbols-outlined notranslate outlined">language</span>Make public`,
                callback: async () => {
                  await withSpinner(
                    cachedFirestore
                      .updateDocument({
                        reference: `communityLevels/${levelDef_.id}`,
                        data: {
                          isPublic: true,
                        },
                      })
                      .then(() => {
                        cachedFirestore.clearCollectionCache("communityLevels");
                        showFeed(creatorId);
                        // cachedFirestore.clearCache()
                      })
                  );
                },
              });
            contentList.push({
              innerHTML: isFavorite()
                ? `<span class="material-symbols-outlined notranslate outlined">heart_minus</span>Remove from favorites`
                : `<span class="material-symbols-outlined notranslate outlined">favorite</span>Add to favorites`,
              callback: (e) => {
                favoriteCallback(e);
                updateDropDown(divItemMoreContainer, getContentList());
              },
            });
            if (isPublic)
              contentList.push({
                innerHTML: `<span class="material-symbols-outlined notranslate outlined">lock</span>Make private`,
                callback: async () => {
                  await withSpinner(
                    cachedFirestore
                      .updateDocument({
                        reference: `communityLevels/${levelDef_.id}`,
                        data: {
                          isPublic: false,
                        },
                      })
                      .then(() => {
                        cachedFirestore.clearCollectionCache("communityLevels");
                        showFeed(creatorId);
                        // cachedFirestore.clearCache()
                      })
                  );
                },
              });
            contentList.push({
              innerHTML: `<span class="material-symbols-outlined notranslate outlined warning">delete</span><span class="warning">Delete</span>`,
              callback: () => {
                levelToDeleteGlob = levelDef_.id;
                deleteLevelDialogEl.style.display = "initial";
              },
            });
          } else {
            contentList.push(
              {
                innerHTML: isFavorite()
                  ? `<span class="material-symbols-outlined notranslate outlined">heart_minus</span>Remove from favorites`
                  : `<span class="material-symbols-outlined notranslate outlined">favorite</span>Add to favorites`,
                callback: (e) => {
                  favoriteCallback(e);
                  updateDropDown(divItemMoreContainer, getContentList());
                },
              },
              {
                innerHTML: `<span class="material-symbols-outlined notranslate outlined warning">report</span><span class="warning">Report Content Violation</span>`,
                callback: () => withSpinner(report(levelDef_)),
              }
            );
            if (__ADMIN__)
              contentList.push({
                innerHTML: `<span class="material-symbols-outlined notranslate outlined warning">delete</span><span class="warning">Admin Delete</span>`,
                callback: () => {
                  levelToDeleteGlob = levelDef_.id;
                  deleteLevelDialogEl.style.display = "initial";
                },
              });
          }
          return contentList;
        };
        const dropDownContainerEl = document.createElement("div");
        dropDownContainerEl.classList.add("community-feed-item-more-container");
        const dropDownEl = addDropDown(
          divItemMoreContainer,
          getContentList(),
          true,
          true
        );
        dropDownContainerEl.append(dropDownEl);
        divItemMoreContainer.classList.add("ripple");
        const spanItemMore = document.createElement("span");
        spanItemMore.classList.add(
          "material-symbols-outlined",
          "community-feed-item-more",
          "text-on-white",
          "notranslate"
        );
        spanItemMore.textContent = "more_vert";
        divItemMoreContainer.appendChild(spanItemMore);
        divItemCaptionTop.appendChild(dropDownContainerEl);

        if (isFavorite()) createFavorite();

        const divItemCaption2 = document.createElement("div");
        const divItemCreatedAt = document.createElement("div");
        divItemCreatedAt.classList.add("community-feed-item-date");
        let dateCreated = createdAt && new Date(createdAt);
        divItemCreatedAt.textContent =
          dateCreated && prettyDateFormat(dateCreated);
        const divItemCaptions = document.createElement("div");

        if (description) {
          const divItemCaption = document.createElement("div");
          const divItemDescription = document.createElement("div");
          divItemDescription.classList.add(
            "community-feed-item-description",
            "text-sec",
            "text-on-white",
            "notranslate"
          );
          divItemDescription.textContent = description;
          divItemCaption.appendChild(divItemDescription);
          divItemCaption.className = "community-feed-item-caption";
          divItemCaptions.appendChild(divItemCaption);
        } else {
          divItem.classList.add("community-feed-item-without-description");
          divItemCaptions.classList.add(
            "community-feed-item-captions-without-description"
          );
        }

        divItemCaptions.classList.add("community-feed-item-captions");
        divItemCaption2.classList.add("community-feed-item-caption");
        divItemCaptions.appendChild(divItemCaption2);
        if (uniqueRatingCount > 0 && avgRating) {
          const divItemStarContainer = document.createElement("div");
          divItemStarContainer.classList.add(
            "community-feed-item-star-container",
            "notranslate"
          );
          const star = document.createElement("span");
          const starHalf = document.createElement("span");
          const spanRatingsRating = document.createElement("span");
          const spanRatingsCounter = document.createElement("span");
          spanRatingsCounter.classList.add(
            "text-sec",
            "community-feed-item-ratings-counter",
            "text-on-white"
          );
          spanRatingsRating.classList.add(
            "text-sec",
            "community-feed-item-ratings-rating",
            "text-on-white"
          );
          spanRatingsRating.textContent = Math.round(avgRating * 10) / 10;
          divItemStarContainer.appendChild(spanRatingsRating);
          spanRatingsCounter.textContent = `(${formatNumber(
            uniqueRatingCount
          )})`;
          star.classList.add(
            "material-symbols-outlined",
            "community-feed-item-star",
            "notranslate"
          );
          starHalf.classList.add(
            "material-symbols-outlined",
            "community-feed-item-star",
            "notranslate"
          );
          star.innerHTML = "star";
          starHalf.innerHTML = "star_half";
          let roundedRating = Math.round(avgRating * 2) / 2;
          let fullStars = Math.floor(roundedRating);
          let halfStars = roundedRating % 1 >= 0.5 ? 1 : 0;

          for (let i = 0; i < fullStars; i++) {
            divItemStarContainer.appendChild(star.cloneNode(true));
          }

          if (halfStars === 1) {
            divItemStarContainer.appendChild(starHalf.cloneNode(true));
            fullStars++;
          }

          for (let i = fullStars; i < 5; i++) {
            const emptyStar = star.cloneNode(true);
            divItemStarContainer.appendChild(emptyStar);
            emptyStar.classList.add("outlined");
          }

          divItemStarContainer.appendChild(spanRatingsCounter);
          divItemCaption2.appendChild(divItemStarContainer);

          if (wonCount + lostCount > 30) {
            const divItemDifficultyContainer = document.createElement("div");
            if (winToLossRatio < 0.1) {
              divItemDifficultyContainer.textContent = "Hard";
              divItemDifficultyContainer.style.color = "darkred";
            } else if (winToLossRatio < 0.5) {
              divItemDifficultyContainer.textContent = "Medium";
              divItemDifficultyContainer.style.color = "coral";
            } else {
              divItemDifficultyContainer.textContent = "Easy";
              divItemDifficultyContainer.style.color = "green";
            }
            divItemDifficultyContainer.classList.add(
              "community-feed-item-difficulty-container",
              "text-sec"
            );
            divItemCaption2.appendChild(divItemDifficultyContainer);
          }
        } else if (new Date() - dateCreated < 1000 * 60 * 60 * 24 * 3) {
          const divItemNewContainer = document.createElement("div");
          divItemNewContainer.classList.add(
            "community-feed-item-new-container",
            "text-sec"
          );
          divItemNewContainer.textContent = "NEW";
          divItemCaption2.appendChild(divItemNewContainer);
        } // else divItemCaption.appendChild(document.createElement("div"));
        else divItemCaption2.appendChild(document.createElement("div"));
        resetLangAttributeOfLeafs(divItemCaption2);
        // divItemCaption.appendChild(divItemCreator);
        divItemCaption2.appendChild(divItemCreatedAt);
        if (thumbnailUrl) {
          const img = document.createElement("img");
          img.src = thumbnailUrl;
          img.style.height = "100%";
          divItemThumbnail.appendChild(img);
        }
        const divCreatorNameContainer = document.createElement("div");
        divCreatorNameContainer.classList.add(
          "community-feed-item-creator-name-container",
          "ripple"
        );
        const divCreatorName = document.createElement("div");
        divCreatorName.classList.add(
          "community-feed-item-creator-name",
          "text-sec",
          "text-on-white",
          "notranslate"
        );
        divCreatorName.textContent = creatorUname || "<null>";
        divCreatorNameContainer.appendChild(divCreatorName);

        const divImgContainer = document.createElement("div");
        const divImgContainerContainer = document.createElement("div");
        divImgContainerContainer.classList.add(
          "community-feed-item-creator-img-container-container"
        );
        divImgContainerContainer.appendChild(divImgContainer);
        addTouchEvent(
          divImgContainer,
          requireAuth(() => withSpinner(showProfile(creatorId)))
        );
        addTouchEvent(
          divCreatorNameContainer,
          requireAuth(() => withSpinner(showProfile(creatorId)))
        );
        divImgContainer.classList.add(
          "community-feed-item-creator-img-container",
          "button-on-white",
          "ripple"
        );
        const img = document.createElement("img");
        img.src = creatorThumbnailUrl || "img/placeholder.jpg";
        img.referrerPolicy = "no-referrer";
        img.onerror = () => {
          img.src = "img/placeholder.jpg";
        };
        img.style.height = "100%";
        divImgContainer.appendChild(img);
        divItemCaptionTop.appendChild(divImgContainerContainer);
        divItemCaptionTop.appendChild(divCreatorNameContainer);

        fragment.appendChild(divItemCaptionTop);
        fragment.appendChild(divItemThumbnail);
        fragment.appendChild(divItemCaptions);
        divItem.appendChild(fragment);
      }
    );
  });
  await yieldWithPriority("user-visible");

  initRipple();
  addClickToBtns();
}

async function report(level) {
  await cachedFirestore.updateDocument({
    reference: `communityLevels/${level.id}`,
    data: {
      reportCount: increment(1),
    },
  });
  showInfoSnackbar("Level reported");
}

async function deleteLevel(levelId) {
  await cachedFirestore.deleteDocument({
    reference: `communityLevels/${levelId}`,
  });
  cachedFirestore.clearCollectionCache("communityLevels");
  cachedFirestore.clearCache(`profile/${store.user.uid}`);
  refreshCommunityFeed();
  refreshProfile();
}

async function deleteSelectedLevel() {
  if (!levelToDeleteGlob) {
    log.error("No level selected to delete");
    return;
  }
  await deleteLevel(levelToDeleteGlob);
  levelToDeleteGlob = null;
  deleteLevelDialogEl.style.display = null;
}

addTouchEvent(
  deleteLevelDialogCancelBtnEl,
  () => (deleteLevelDialogEl.style.display = null)
);
addTouchEvent(deleteLevelDialogDeleteBtnEl, requireAuth(deleteSelectedLevel));

addTouchEvent(gameProfileEl, () => {
  const levelDefCopy = JSON.parse(JSON.stringify(levelDef));
  const gameStateCopy = gameState.copy();
  const gameFramesCopy = [...store.gameFrames];
  const screenState = {
    levelDef: levelDefCopy,
    gameState: gameStateCopy,
    gameFrames: gameFramesCopy,
    step: store.step,
  };
  stack.updateScreenState(screenState);
  showProfile(levelDef.metadata.creatorId);
});
